import { Dialog } from '@components/dialog';
import { Box, Typography, Grid } from '@mui/material';
import { format } from 'date-fns';
import { getDateFormatter, LongDateTimeFormat } from '@utils/date';
import { useUpdateUnderwritingActivity } from '@services/underwritingReferral/useUpdateUnderwritingActivity';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { UnderwritingActivityItem } from '@models/underwritingReferral';
import { useCreateUnderwritingActivity } from '@services/underwritingReferral/useCreateUnderwritingActivity';
import {
  TUnderwritingActivityFormValues,
  UnderwritingActivityFormWithMethods,
} from '@pages/QuoteDetails/sidebarItems/underwriting/UnderwritingActivityForm';

export const EditUWActivityDialog = ({
  editOpen,
  setEditOpen,
  selectedActivity,
  quoteId,
}: {
  editOpen: boolean;
  setEditOpen: (open: boolean) => void;
  selectedActivity: UnderwritingActivityItem | null;
  quoteId: string;
}) => {
  const methods = useForm<TUnderwritingActivityFormValues>({
    defaultValues: {
      resolve: false,
      resolutionNote: selectedActivity?.note || '',
      resolutionReasonId: undefined,
      description: selectedActivity?.description || '',
      type: selectedActivity?.underwritingActivityType?.id || '',
    },
  });

  const [updateError, setUpdateError] = useState<string | null>(null);

  const { updateUnderwritingActivity, isPending: isUpdating } = useUpdateUnderwritingActivity();
  const { createUnderwritingActivity, isPending: isCreating } = useCreateUnderwritingActivity();

  const isPending = isUpdating || isCreating;

  const [layoutPreferences] = useStore((state) => [state.layoutPreferences], shallow);
  const clockFormat = layoutPreferences.clockFormat;

  const handleSave = async () => {
    try {
      const activity = methods.getValues();

      if (selectedActivity) {
        const body = {
          id: selectedActivity?.id ?? '',
          note: activity.resolutionNote ?? '',
          resolutionReasonId: activity.resolutionReasonId ?? undefined,
        };
        await updateUnderwritingActivity(body);
      } else {
        const body = {
          underwritingActivityTypeId: activity?.type ?? '',
          description: activity.description ?? '',
          quoteId,
        };

        await createUnderwritingActivity(body);
      }
      setEditOpen(false);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update activity';
      setUpdateError(errorMessage);
    }
  };

  // FUTURE FULLSCREEN TODO: this needs more details to match card, ideally there would be a more generic reusable logic between these two so when 'Note' or 'Resolve Reason' is added it just shows here.
  const subjectivityDetails = selectedActivity
    ? [
        {
          label: 'Start Date',
          value: selectedActivity.createdAt
            ? format(new Date(selectedActivity.createdAt), getDateFormatter(clockFormat, LongDateTimeFormat))
            : '-',
        },
        {
          label: 'Reason',
          value: selectedActivity.underwritingActivityType?.label || '-',
        },
        {
          label: 'Description',
          value: selectedActivity.description || '-',
        },
      ]
    : [];

  const isDirty = methods.formState.isDirty;
  const isResolvedActivity = Boolean(selectedActivity?.resolvedAt);

  return (
    <Dialog
      open={editOpen}
      handleCloseDialog={() => setEditOpen(false)}
      title={selectedActivity ? (selectedActivity.resolvedAt ? 'View Activity' : 'Update Activity') : 'Add Activity'}
      primaryBtnText={selectedActivity ? (isResolvedActivity ? undefined : 'Save') : 'Create'}
      primaryBtnAction={handleSave}
      secondaryBtnText={isResolvedActivity ? 'Close' : 'Cancel'}
      secondaryBtnAction={() => setEditOpen(false)}
      isPrimaryBtnDisabled={!methods.formState.isValid || !isDirty || isPending}
      isPrimaryBtnLoading={isPending}
    >
      <Box sx={{ p: 2, minWidth: 350 }}>
        {selectedActivity && (
          <Box sx={{ mb: 2 }}>
            <Typography variant='subtitle2' sx={{ mb: 1 }}>
              Details
            </Typography>
            <Grid container direction='column' spacing={1} sx={{ background: '#f5f5f5', borderRadius: 1, p: 2 }}>
              {subjectivityDetails.map((detail) => (
                <Grid key={detail.label}>
                  <Typography variant='bodySmall'>
                    <b>{detail.label}:</b> {detail.value}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
        {!isResolvedActivity && (
          <Box sx={{ mb: 2 }}>
            {selectedActivity && (
              <Typography variant='subtitle2' sx={{ mb: 1 }}>
                Actions
              </Typography>
            )}
            <UnderwritingActivityFormWithMethods activity={selectedActivity || undefined} methods={methods} />
          </Box>
        )}
        {updateError && (
          <Box color='error.main' mt={2}>
            {updateError}
          </Box>
        )}
      </Box>
    </Dialog>
  );
};
